import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { SecureStorage, KeychainAccess } from '@aparajita/capacitor-secure-storage';

/**
 * Encrypted on-device store for documents the user has explicitly marked
 * "available offline".
 *
 * Shape of the guarantee, stated plainly so nobody assumes more than it gives:
 *
 * - Every file is encrypted with AES-GCM under a random 256-bit key that lives
 *   in the platform keychain/keystore with `whenUnlockedThisDeviceOnly`. The key
 *   is unreadable while the DEVICE is locked, and it never leaves the device, so
 *   a backup restored onto another handset decrypts nothing.
 * - It does NOT make files unreadable while the Doqit app lock is engaged. That
 *   lock is Redux state (`common/store/lockSlice.ts`) and cannot protect bytes on
 *   disk. Dan's decision 2026-08-03, over a biometric-gated key.
 * - Plaintext is never written to disk. Decryption yields an ArrayBuffer that the
 *   viewer renders from memory. This is only possible because the document
 *   viewers were changed to render from bytes rather than from a URL.
 *
 * Everything here is native-only and never throws to the caller, matching the
 * discipline in `sharedPreferences.ts`. On web every call is a no-op.
 */

const KEY_ALIAS = 'doqit.offlineVault.key.v1';
const ROOT = 'doqit-offline';
/** Not iCloud-backed. Encrypted-and-backed-up would still be wasteful, and the
 *  device-only key would make a restored copy useless anyway. */
const DIRECTORY = Directory.LibraryNoCloud;

/** A single document may not hoard the whole allowance. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
/** Total across every offline document. */
export const MAX_TOTAL_BYTES = 250 * 1024 * 1024;

const tooLargeMessage = (name: string) => `"${name}" is over 25MB, the most a single file can be to save for offline use.`;

export type VaultErrorCode = 'file-too-large' | 'quota-exceeded' | 'unavailable' | 'failed';

export class VaultError extends Error {
  code: VaultErrorCode;

  constructor(code: VaultErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = 'VaultError';
  }
}

export interface VaultFile {
  name: string;
  bytes: ArrayBuffer;
}

export interface VaultEntry {
  docId: number;
  bytes: number;
  cachedAt: number;
  updatedAt: string | null;
}

interface StoredIndexEntry {
  docId: number;
  bytes: number;
  cachedAt: number;
  updatedAt: string | null;
  files: string[];
}

// Native platform AND the keychain plugin actually compiled into this build.
// The SecureStorage pod entered the Podfile late (2026-08-04): a native build
// made without it has no keychain module, every call rejects "not
// implemented", and the toggle showed an error it could never recover from
// (Dan's device, 2026-08-05). With this check such builds simply hide the
// offline feature - the honest degrade.
export const isVaultSupported = (): boolean => (
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('SecureStorage')
);

// ---------------------------------------------------------------------------
// Key handling
// ---------------------------------------------------------------------------

let keyPromise: Promise<CryptoKey | null> | null = null;

const toBase64 = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let s = '';
  // Chunked so a large file cannot blow the argument limit of String.fromCharCode.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(s);
};

const fromBase64 = (b64: string): Uint8Array => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
};

/** The underlying reason the last key fetch failed - surfaced through the
 *  offlineSaveFailed analytics event so a tester's "could not be unlocked"
 *  report is diagnosable without their Xcode console. */
let lastKeyErrorValue = '';
export const getLastKeyError = (): string => lastKeyErrorValue;

const mintKey = async (): Promise<CryptoKey> => {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  await SecureStorage.set(
    KEY_ALIAS,
    toBase64(raw.buffer),
    false,
    false,
    // The accessibility class IS the security property here. Do not relax it
    // to `whenUnlocked` (which syncs to other devices via encrypted backup)
    // without revisiting the guarantee documented at the top of this file.
    KeychainAccess.whenUnlockedThisDeviceOnly,
  );
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
};

const loadKey = async (): Promise<CryptoKey | null> => {
  if (!isVaultSupported()) return null;
  lastKeyErrorValue = '';
  try {
    let existing: unknown = null;
    try {
      // MUST be get(), not getItem(): set() stores JSON.stringify(data), so
      // the low-level getItem() returns the base64 WITH surrounding quotes
      // and atob() throws. That asymmetry made the vault work for exactly
      // one session (fresh key used from memory) and fail on every launch
      // after - the entire 2026-08-05 offline saga. get() is set()'s true
      // inverse (JSON.parse). convertDate=false so a date-like string can
      // never come back as a Date.
      existing = await SecureStorage.get(KEY_ALIAS, false);
    } catch (readError) {
      // The stored key exists but the keychain refused to read it (seen in
      // the field after entitlement/provisioning changes: the old item lives
      // in an access group this build can no longer open). Without the key
      // every cached file is undecryptable garbage anyway, so self-heal:
      // wipe the vault and fall through to minting a fresh key. Fail-closed
      // is preserved - nothing readable survives, the user just re-saves.
      console.error('offlineVault: stored key unreadable, resetting vault', readError);
      await Filesystem.rmdir({ path: ROOT, directory: DIRECTORY, recursive: true }).catch(() => undefined);
      await SecureStorage.remove(KEY_ALIAS).catch(() => undefined);
    }
    if (typeof existing === 'string' && existing.length > 0) {
      const imported = await crypto.subtle.importKey('raw', fromBase64(existing), 'AES-GCM', false, ['encrypt', 'decrypt']);
      return imported;
    }
    return await mintKey();
  } catch (error) {
    console.error('offlineVault: could not obtain encryption key', error);
    lastKeyErrorValue = error instanceof Error ? `${error.name}: ${error.message}`.slice(0, 200) : String(error).slice(0, 200);
    return null;
  }
};

const getKey = (): Promise<CryptoKey | null> => {
  if (!keyPromise) keyPromise = loadKey();
  // A failed fetch must not poison the session: cache only success, so the
  // next attempt retries the keychain instead of replaying a stale null
  // (e.g. a fetch that raced the device unlocking).
  return keyPromise.then((key) => {
    if (key === null) keyPromise = null;
    return key;
  });
};

// ---------------------------------------------------------------------------
// Crypto
// ---------------------------------------------------------------------------

/** Fresh IV per payload. Never reuse one under the same key. */
const encrypt = async (key: CryptoKey, data: ArrayBuffer): Promise<string> => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  const joined = new Uint8Array(iv.length + cipher.byteLength);
  joined.set(iv, 0);
  joined.set(new Uint8Array(cipher), iv.length);
  return toBase64(joined.buffer);
};

const decrypt = async (key: CryptoKey, payload: string): Promise<ArrayBuffer> => {
  const joined = fromBase64(payload);
  const iv = joined.subarray(0, 12);
  const cipher = joined.subarray(12);
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
};

// ---------------------------------------------------------------------------
// Index
// ---------------------------------------------------------------------------

const indexPath = () => `${ROOT}/index.json`;
const docDir = (docId: number) => `${ROOT}/${docId}`;
const filePath = (docId: number, n: number) => `${docDir(docId)}/${n}.bin`;
const metaPath = (docId: number) => `${docDir(docId)}/meta.bin`;

const readIndex = async (): Promise<StoredIndexEntry[]> => {
  try {
    const res = await Filesystem.readFile({ path: indexPath(), directory: DIRECTORY });
    // Decoded as UTF-8, not via atob. See writeIndex for why.
    const text = typeof res.data === 'string'
      ? new TextDecoder().decode(fromBase64(res.data))
      : '';
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Absent index simply means nothing has been cached yet.
    return [];
  }
};

const writeIndex = async (entries: StoredIndexEntry[]): Promise<void> => {
  // UTF-8 encoded before base64, NOT btoa. btoa throws InvalidCharacterError on
  // any character above U+00FF, and this JSON carries file names: a document
  // called "賃貸借契約書.pdf" would have thrown here, been swallowed by
  // putDocument's catch, and reported to the user as "that document could not
  // be saved for offline use" with no way to tell why.
  await Filesystem.writeFile({
    path: indexPath(),
    directory: DIRECTORY,
    data: toBase64(new TextEncoder().encode(JSON.stringify(entries)).buffer),
    recursive: true,
  });
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const listCached = async (): Promise<VaultEntry[]> => {
  if (!isVaultSupported()) return [];
  try {
    return (await readIndex()).map(({
      docId, bytes, cachedAt, updatedAt,
    }) => ({
      docId, bytes, cachedAt, updatedAt,
    }));
  } catch (error) {
    console.error('offlineVault: could not list cached documents', error);
    return [];
  }
};

export const isCached = async (docId: number): Promise<boolean> => {
  if (!isVaultSupported()) return false;
  return (await readIndex()).some((e) => e.docId === docId);
};

export const removeDocument = async (docId: number): Promise<void> => {
  if (!isVaultSupported()) return;
  try {
    await Filesystem.rmdir({ path: docDir(docId), directory: DIRECTORY, recursive: true })
      .catch(() => undefined);
    await writeIndex((await readIndex()).filter((e) => e.docId !== docId));
  } catch (error) {
    console.error('offlineVault: could not remove document', error);
  }
};

export const putDocument = async (
  docId: number,
  meta: unknown,
  files: VaultFile[],
  updatedAt: string | null = null,
): Promise<void> => {
  if (!isVaultSupported()) throw new VaultError('unavailable', 'Offline storage is not available here.');

  const key = await getKey();
  if (!key) throw new VaultError('unavailable', 'Offline storage could not be unlocked on this device.');

  const total = files.reduce((sum, f) => sum + f.bytes.byteLength, 0);
  const oversize = files.find((f) => f.bytes.byteLength > MAX_FILE_BYTES);
  if (oversize) {
    throw new VaultError('file-too-large', tooLargeMessage(oversize.name));
  }

  const index = await readIndex();
  const others = index.filter((e) => e.docId !== docId);
  const used = others.reduce((sum, e) => sum + e.bytes, 0);
  if (used + total > MAX_TOTAL_BYTES) {
    throw new VaultError('quota-exceeded', 'There is not enough offline space. Remove a document first.');
  }

  try {
    // Replace rather than merge, so a re-save after the document changed cannot
    // leave stale files from the previous version behind.
    await Filesystem.rmdir({ path: docDir(docId), directory: DIRECTORY, recursive: true })
      .catch(() => undefined);

    await Filesystem.writeFile({
      path: metaPath(docId),
      directory: DIRECTORY,
      data: await encrypt(key, new TextEncoder().encode(JSON.stringify(meta)).buffer),
      recursive: true,
    });

    for (let i = 0; i < files.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const payload = await encrypt(key, files[i].bytes);
      // eslint-disable-next-line no-await-in-loop
      await Filesystem.writeFile({
        path: filePath(docId, i),
        directory: DIRECTORY,
        data: payload,
        recursive: true,
      });
    }

    await writeIndex([...others, {
      docId,
      bytes: total,
      cachedAt: Date.now(),
      updatedAt,
      files: files.map((f) => f.name),
    }]);
  } catch (error) {
    console.error('offlineVault: could not save document', error);
    // Never leave a half-written document behind claiming to be available.
    await removeDocument(docId);
    throw new VaultError('failed', 'That document could not be saved for offline use.');
  }
};

/**
 * Every cached document's decrypted META only - file bytes are never read.
 * Cheap enough to run on each offline search keystroke; powers the offline
 * search fallback (a saved doc must be findable with no signal, 869eafpzw).
 */
export const listCachedMeta = async (): Promise<Array<{ docId: number; meta: unknown }>> => {
  if (!isVaultSupported()) return [];
  const key = await getKey();
  if (!key) return [];
  const out: Array<{ docId: number; meta: unknown }> = [];
  const entries = await readIndex();
  for (let i = 0; i < entries.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const metaRaw = await Filesystem.readFile({ path: metaPath(entries[i].docId), directory: DIRECTORY });
      // eslint-disable-next-line no-await-in-loop
      const meta = JSON.parse(new TextDecoder().decode(await decrypt(key, metaRaw.data as string)));
      out.push({ docId: entries[i].docId, meta });
    } catch {
      // One unreadable entry never hides the rest.
    }
  }
  return out;
};

export const getDocument = async (
  docId: number,
): Promise<{ meta: unknown; files: VaultFile[]; cachedAt: number } | null> => {
  if (!isVaultSupported()) return null;

  const key = await getKey();
  if (!key) return null;

  try {
    const entry = (await readIndex()).find((e) => e.docId === docId);
    if (!entry) return null;

    const metaRaw = await Filesystem.readFile({ path: metaPath(docId), directory: DIRECTORY });
    const meta = JSON.parse(
      new TextDecoder().decode(await decrypt(key, metaRaw.data as string)),
    );

    const files: VaultFile[] = [];
    for (let i = 0; i < entry.files.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await Filesystem.readFile({ path: filePath(docId, i), directory: DIRECTORY });
      // eslint-disable-next-line no-await-in-loop
      files.push({ name: entry.files[i], bytes: await decrypt(key, res.data as string) });
    }

    return { meta, files, cachedAt: entry.cachedAt };
  } catch (error) {
    // A decryption failure means the key no longer matches what is on disk, so
    // the cache is unreadable. Fail CLOSED and drop it rather than pretending
    // the document is available offline when it cannot be opened.
    console.error('offlineVault: could not read document, dropping it', error);
    await removeDocument(docId);
    return null;
  }
};

/**
 * Destroys every cached document AND the key.
 *
 * Called at the same three points the Spotlight index and widget snapshot are
 * cleared: the `clearCredentials` reducer, MenuBar's `onLogoutHandler` (which
 * genuinely does not dispatch `clearCredentials`), and `deleteUserAction`. All
 * three are needed; any one alone leaves a signed-out device holding readable
 * copies of the previous user's documents.
 */
export const wipeAll = async (): Promise<void> => {
  if (!isVaultSupported()) return;
  try {
    await Filesystem.rmdir({ path: ROOT, directory: DIRECTORY, recursive: true })
      .catch(() => undefined);
  } catch (error) {
    console.error('offlineVault: could not remove cached files', error);
  }
  try {
    await SecureStorage.remove(KEY_ALIAS);
  } catch (error) {
    console.error('offlineVault: could not destroy encryption key', error);
  }
  // Force the next use to mint a fresh key rather than reusing the destroyed one.
  keyPromise = null;
};

/**
 * Downloads a document's files and stores them encrypted.
 *
 * Shared by the offline toggle and the freshness refresh so the two cannot
 * drift in what they save. Fetching happens here because a presigned URL is
 * only valid for about ten minutes: the bytes are the thing worth keeping,
 * never the URL.
 */
export const cacheDocument = async (
  docId: number,
  meta: unknown,
  files: { url: string; name: string }[],
  updatedAt: string | null,
): Promise<void> => {
  const fetched: VaultFile[] = [];
  for (let i = 0; i < files.length; i += 1) {
    // NATIVE http, never webview fetch. The doc payload's file URLs are
    // presigned with ResponseContentDisposition=attachment, and WebKit
    // CANCELS fetch() of attachment-disposition responses ("TypeError:
    // Load failed") - the root cause of every "could not be saved" report
    // (found 2026-08-05 via simulator console capture). CapacitorHttp runs
    // on the native side, immune to that and to CORS. responseType 'blob'
    // returns the body base64-encoded.
    // eslint-disable-next-line no-await-in-loop
    const res = await CapacitorHttp.get({ url: files[i].url, responseType: 'blob' });
    if (res.status !== 200 || typeof res.data !== 'string' || res.data.length === 0) {
      throw new VaultError('failed', `Could not download ${files[i].name}`);
    }
    // Reject oversize files from the base64 length, BEFORE decoding. Uploads
    // allow 50MB but offline holds 25MB, and decoding a 50MB file only to
    // refuse it doubles the memory spike that can crash the webview.
    if (Math.floor((res.data.length * 3) / 4) > MAX_FILE_BYTES) {
      throw new VaultError('file-too-large', tooLargeMessage(files[i].name));
    }
    fetched.push({ name: files[i].name, bytes: fromBase64(res.data).buffer });
  }
  await putDocument(docId, meta, fetched, updatedAt);
};

/**
 * Total bytes currently held, for the settings screen. Without this the
 * quota error tells a user to remove a document while giving them no way to
 * see what is stored.
 */
export const cachedBytes = async (): Promise<number> => (await listCached())
  .reduce((sum, e) => sum + e.bytes, 0);

/**
 * Rewrites a cached document's metadata without touching its files.
 *
 * `updated_at` changes on ANY edit, including a rename. Without this the
 * freshness refresh would re-download every file to capture a changed name -
 * potentially tens of megabytes over mobile data to learn one string. The
 * caller compares file lists and uses this when only the metadata moved.
 */
export const updateCachedMeta = async (
  docId: number,
  meta: unknown,
  updatedAt: string | null,
): Promise<boolean> => {
  if (!isVaultSupported()) return false;
  const key = await getKey();
  if (!key) return false;
  try {
    const index = await readIndex();
    const entry = index.find((e) => e.docId === docId);
    if (!entry) return false;
    await Filesystem.writeFile({
      path: metaPath(docId),
      directory: DIRECTORY,
      data: await encrypt(key, new TextEncoder().encode(JSON.stringify(meta)).buffer),
      recursive: true,
    });
    await writeIndex(index.map((e) => (e.docId === docId ? { ...e, updatedAt } : e)));
    return true;
  } catch (error) {
    console.error('offlineVault: could not refresh metadata', error);
    return false;
  }
};

/** The file names held for a document, so a caller can tell whether a change
 *  touched the files or only the metadata. */
export const cachedFileNames = async (docId: number): Promise<string[] | null> => {
  if (!isVaultSupported()) return null;
  const entry = (await readIndex()).find((e) => e.docId === docId);
  return entry ? entry.files : null;
};
